import jwt_decode from "jwt-decode";
import { JWT_ROLES } from "../constants";
import { Nullable, RX_JWT_PAYLOAD } from "../types";
import authorizationStore from "../auth/authorizationStore";
import PublicApiService from "../../services/PublicApiService";

const getJWTPayload = (token: string): RX_JWT_PAYLOAD => {
  return jwt_decode(token);
};

const isLoggedUserRxAdmin = (token: string): boolean => {
  const payload = getJWTPayload(token);

  if (Array.isArray(payload.role)) {
    return !!payload.role.find(role => role === JWT_ROLES.RX_ADMIN);
  }
  return !![JWT_ROLES.RX_ADMIN].find(role => role === payload.role);
};

const getBuIdsFromToken = (token: string) => {
  const payload = getJWTPayload(token);

  if (!payload.rxBu) {
    return null;
  }

  if(Array.isArray(payload.rxBu)) {
    return payload.rxBu;
  }
  return [payload.rxBu];
}

const isLoggedUserBUAdmin = (token: string): boolean => {
  const payload = getJWTPayload(token);
  return Boolean(payload.rxBu);
};

const getStaffOnSiteEe = (token: string) =>{
  const payload = getJWTPayload(token);

  if (!payload.rxOnsiteEe) {
    return null;
  }
  if (Array.isArray(payload.rxOnsiteEe)) {
    return payload.rxOnsiteEe;
  }
  return [payload.rxOnsiteEe];
}

const getEventsFromToken = (token: string, payloadKey: keyof RX_JWT_PAYLOAD): Nullable<string[]> =>{
  const payload = getJWTPayload(token);

  if (!payload[payloadKey]) {
    return null;
  }
  if (Array.isArray(payload[payloadKey])) {
    return payload[payloadKey] as string[];
  }
  return [payload[payloadKey]] as string[];
}

const getRxOnsiteClaim = (token: string) => {
  const payload = getJWTPayload(token);
  const claims = payload.rxOnsiteEe;
  return Array.isArray(claims) ? claims[0] : claims;
};

const isLoggerUserTempStaff = (token: string, eventEditionGbsCode: string) => {
  const payload = getJWTPayload(token);

  if (!payload.rxOnsiteEe) {
    return false;
  }

  if (Array.isArray(payload.rxOnsiteEe)) {
    return !!payload.rxOnsiteEe.find(claim => claim === eventEditionGbsCode);
  }
  return payload.rxOnsiteEe === eventEditionGbsCode;
};

const hasValidRole = (token: string, eventEditionGbsCode: string, key: keyof RX_JWT_PAYLOAD) => {
  const payload = getJWTPayload(token);

  if (Array.isArray(payload[key])) {
    return !!(payload[key] as string[]).find(claim => claim === eventEditionGbsCode);
  }
  return payload[key] === eventEditionGbsCode;
};

const isLoggedUserManager = (token: string, eventEditionGbsCode: string) => {
  return hasValidRole(token, eventEditionGbsCode, 'rxOnsiteManagerEe');
}

const isLoggedUserFinance = (token: string, eventEditionGbsCode: string) => {
  return hasValidRole(token, eventEditionGbsCode, 'rxOnsiteFinanceEe');
}

const getContactIdFromToken = async () => {
  const token = await authorizationStore.getToken();
  const decoded = jwt_decode<string>(token);
  return `CHBX_OBU_000-USR-${decoded.sub}`;
};

const getEventEditionByToken = async (tempUserShows: RX_JWT_PAYLOAD["rxOnsiteEe"]) => {
  const defaultEventEditionId = process.env.REACT_APP_EVENT_EDITION_ID as string;
  const showListResponse = await PublicApiService.getSupportedMercuryShows();
  const showList = showListResponse.eventEditions;
  const event = showList.find(show => show.gbsCode === tempUserShows);

  if (event) {
    return event.atlasId;
  }
  return defaultEventEditionId;
};

const jwtUtils = {
  getJWTPayload,
  isLoggedUserFinance,
  isLoggedUserManager,
  isLoggedUserRxAdmin,
  isLoggedUserBUAdmin,
  isLoggerUserTempStaff,
  getContactIdFromToken,
  getRxOnsiteClaim,
  getEventEditionByToken,
  getBuIdsFromToken,
  getStaffOnSiteEe,
  getEventsFromToken
};

export default jwtUtils;
